<script lang="ts" setup>
  import { ref, watch } from 'vue'
  import {
    DEFAULT_TITLE,
    TOGGLE_EVENT_NAME,
    TEST_ID,
    CONTENT_TEST_ID,
    SUMMARY_TEST_ID,
  } from './BaseFoldingBlock.constants'
  import ArrowIcon from '@/shared/components/icons/ArrowIcon.vue'

  interface Props {
    title?: string
    isOpen?: boolean
    canFold?: boolean
  }
  interface Emits {
    (e: typeof TOGGLE_EVENT_NAME, isOpen: boolean): void
  }

  const props = withDefaults(defineProps<Props>(), {
    title: DEFAULT_TITLE,
    canFold: true,
  })

  const emit = defineEmits<Emits>()

  const isOpen = ref(!!props.isOpen)
  const toggle = () => {
    if (!props.canFold) return

    isOpen.value = !isOpen.value
    emit(TOGGLE_EVENT_NAME, isOpen.value)
  }

  watch(
    () => props.isOpen,
    (newValue) => {
      isOpen.value = newValue
    }
  )
</script>

<template>
  <div class="base-folding-block" :data-testid="TEST_ID">
    <div
      class="base-folding-block__summary"
      :class="{ 'base-folding-block__summary--disabled': !canFold }"
      :data-testid="SUMMARY_TEST_ID"
      @click="toggle"
    >
      <div
        v-if="canFold"
        class="base-folding-block__arrow"
        :class="{
          'base-folding-block__arrow--rotate': isOpen,
        }"
      >
        <ArrowIcon />
      </div>
      <div class="base-folding-block__title">
        <slot name="title">{{ props.title }}</slot>
      </div>
      <div class="base-folding-block__summary-content">
        <slot name="after" />
      </div>
    </div>
    <Transition name="fade">
      <div v-if="isOpen" class="base-folding-block__content" :data-testid="CONTENT_TEST_ID">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
  @import './BaseFoldingBlock';
</style>
